import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "input", "fileList", "submitBtn", "form"]

  connect() {
    this.files = []
  }

  // Drag events
  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-emerald-500", "bg-emerald-50", "dark:bg-emerald-900/20")
    this.dropzoneTarget.classList.remove("border-gray-300", "dark:border-gray-600")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-emerald-500", "bg-emerald-50", "dark:bg-emerald-900/20")
    this.dropzoneTarget.classList.add("border-gray-300", "dark:border-gray-600")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-emerald-500", "bg-emerald-50", "dark:bg-emerald-900/20")
    this.dropzoneTarget.classList.add("border-gray-300", "dark:border-gray-600")

    const droppedFiles = Array.from(event.dataTransfer.files)
    this.addFiles(droppedFiles)
  }

  // File input change
  selectFiles(event) {
    const selectedFiles = Array.from(event.target.files)
    this.addFiles(selectedFiles)
    // Reset input so same file can be re-selected
    event.target.value = ""
  }

  addFiles(newFiles) {
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
      "text/csv",
      "image/png",
      "image/jpeg",
      "image/gif",
      "image/webp"
    ]

    const maxSize = 100 * 1024 * 1024 // 100MB

    newFiles.forEach(file => {
      // Check duplicate
      if (this.files.some(f => f.name === file.name && f.size === file.size)) return
      // Check type
      if (!allowed.includes(file.type)) {
        alert(`지원하지 않는 파일 형식입니다: ${file.name}`)
        return
      }
      // Check size
      if (file.size > maxSize) {
        alert(`파일 크기가 100MB를 초과합니다: ${file.name}`)
        return
      }
      this.files.push(file)
    })

    this.renderFileList()
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.files.splice(index, 1)
    this.renderFileList()
  }

  renderFileList() {
    const list = this.fileListTarget

    if (this.files.length === 0) {
      list.innerHTML = ""
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
      return
    }

    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")

    list.innerHTML = this.files.map((file, i) => `
      <div class="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <svg class="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <span class="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">${this.escapeHtml(file.name)}</span>
        <span class="text-xs text-gray-400 flex-shrink-0">${this.formatSize(file.size)}</span>
        <button type="button" data-index="${i}" data-action="click->file-drop#removeFile" class="p-0.5 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join("")
  }

  submit(event) {
    event.preventDefault()
    if (this.files.length === 0) return

    const form = this.formTarget
    const formData = new FormData(form)

    // Remove any existing file entries
    formData.delete("files[]")

    // Append all files
    this.files.forEach(file => {
      formData.append("files[]", file)
    })

    // Submit via fetch with Turbo redirect
    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        "Accept": "text/vnd.turbo-stream.html, text/html"
      }
    }).then(response => {
      if (response.redirected) {
        window.Turbo.visit(response.url)
      } else {
        response.text().then(html => {
          if (response.ok) window.Turbo.visit(window.location.href)
        })
      }
    })
  }

  formatSize(bytes) {
    const units = ["B", "KB", "MB", "GB"]
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
